import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_CLASS_OPTIONS = ['YKS 9.Sınıf', 'YKS 10.Sınıf', 'YKS 11.Sınıf', 'YKS 12.Sınıf', 'YKS Mezun', 'LGS'];
const DEFAULT_FIELD_OPTIONS = ['Sayısal', 'Eşit Ağırlık', 'Sözel', 'Dil', 'KPSS Lisans', 'KPSS Ön Lisans', 'KPSS Ortaöğretim', 'AGS ÖABT'];

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [classLevel, setClassLevel] = useState('');
  const [fieldBranch, setFieldBranch] = useState('');
  const [notRobot, setNotRobot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-settings`).then((response) => setSettings(response.data || {})).catch(() => {});
  }, []);

  const classOptions = useMemo(() => {
    const options = settings?.register_class_options;
    return Array.isArray(options) && options.length > 0 ? options : DEFAULT_CLASS_OPTIONS;
  }, [settings]);

  const fieldOptions = useMemo(() => {
    const options = settings?.register_field_options;
    return Array.isArray(options) && options.length > 0 ? options : DEFAULT_FIELD_OPTIONS;
  }, [settings]);

  const registerDisabled = useMemo(
    () =>
      loading ||
      !name.trim() ||
      !email.trim() ||
      !phone.trim() ||
      !password.trim() ||
      (!classLevel && !fieldBranch) ||
      !notRobot,
    [classLevel, email, fieldBranch, loading, name, notRobot, password, phone]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!notRobot) {
      toast.error('Lütfen "Ben robot değilim" kutusunu işaretleyin.');
      return;
    }

    if (!classLevel && !fieldBranch) {
      toast.error('Sınıf veya alan bilgilerinden en az birini seçmelisiniz.');
      return;
    }

    setLoading(true);
    const result = await register(email, password, name, phone, classLevel, fieldBranch);
    setLoading(false);

    if (result.success) {
      toast.success('Hesabın başarıyla oluşturuldu.');
      navigate('/dashboard');
      return;
    }

    toast.error(result.error);
  };

  return (
    <div className="min-h-screen bg-[#050505] grid grid-cols-1 lg:grid-cols-2">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/d9bd63b7-d6a6-43cd-a29b-1a59dd534490/images/d9d097cd520df86f7eb413c9bbf5b0402cd9aed917b105693a21b63261712c59.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-12">
          <div className="text-center">
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Karga Akademi&apos;ye Katıl
            </h2>
            <p className="text-lg text-[#A1A1AA]">Sana özel çizelgen hazır. Hemen başla.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#B026FF] transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> Ana Sayfaya Dön
          </Link>

          <div className="mb-8">
            <Link to="/" className="mb-6 flex items-center pl-1">
              <span className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KARGA AKADEMİ
              </span>
            </Link>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Hesap Oluştur
            </h1>
            <p className="text-[#A1A1AA]">Karga Akademi ailesine katıl, hedeflerine ulaş.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-white mb-2 block">
                Ad Soyad
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                className="bg-[#0A0A0A] border-white/10 focus:border-[#B026FF] focus:ring-1 focus:ring-[#B026FF] text-white"
                placeholder="Adınız Soyadınız"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-white mb-2 block">
                E-posta
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                className="bg-[#0A0A0A] border-white/10 focus:border-[#B026FF] focus:ring-1 focus:ring-[#B026FF] text-white"
                placeholder="ornek@email.com"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-white mb-2 block">
                Telefon
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                required
                className="bg-[#0A0A0A] border-white/10 focus:border-[#B026FF] focus:ring-1 focus:ring-[#B026FF] text-white"
                placeholder="555 555 55 55"
              />
              <p className="mt-2 text-xs text-[#71717A]">Numara otomatik olarak +90 formatında kaydedilir.</p>
            </div>

            <div>
              <Label className="text-white mb-2 block">Sınıf Seçin</Label>
              <Select value={classLevel} onValueChange={setClassLevel}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                  <SelectValue placeholder="Sınıf seçin" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {classOptions.map((option) => (
                    <SelectItem key={option} value={option} className="text-white">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">Alan Bölüm Seçin</Label>
              <Select value={fieldBranch} onValueChange={setFieldBranch}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                  <SelectValue placeholder="Alan seçin" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {fieldOptions.map((option) => (
                    <SelectItem key={option} value={option} className="text-white">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="password" className="text-white mb-2 block">
                Şifre
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                className="bg-[#0A0A0A] border-white/10 focus:border-[#B026FF] focus:ring-1 focus:ring-[#B026FF] text-white"
                placeholder="••••••••"
              />
            </div>

            <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 cursor-pointer">
              <Checkbox
                checked={notRobot}
                onCheckedChange={(value) => setNotRobot(Boolean(value))}
                className="border-white/30 data-[state=checked]:bg-[#B026FF] data-[state=checked]:border-[#B026FF]"
              />
              <span className="text-sm text-white">Ben robot değilim</span>
            </label>

            <Button type="submit" disabled={registerDisabled} className="w-full bg-[#B026FF] hover:bg-[#C966FF] text-white shadow-lg shadow-[#B026FF]/20">
              {loading ? 'Hesap oluşturuluyor...' : 'Kaydol'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="rounded-lg border border-[#B026FF]/30 bg-[#B026FF]/10 px-4 py-3 text-sm text-[#F5E8FF]">
              🔐 BU SİTE MUKASOFT İLE KORUNMAKTADIR 💕
            </p>
            <p className="text-[#A1A1AA]">
              Zaten hesabın var mı?{' '}
              <Link to="/login" className="text-[#B026FF] hover:text-[#C966FF] font-semibold">
                Giriş Yap
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
